import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { TIERS, type TierSlug } from "@/lib/payments/tiers";

async function getSupabaseAdmin() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin;
}

async function assertAdmin(supabase: any, userId: string) {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden: admin only");
}

async function listAllAuthUsers(supabaseAdmin: any) {
  const users: any[] = [];
  const perPage = 1000;
  for (let page = 1; page <= 20; page += 1) {
    const { data, error } = await supabaseAdmin.auth.admin.listUsers({ page, perPage });
    if (error) throw new Error(error.message);
    users.push(...(data?.users ?? []));
    if ((data?.users?.length ?? 0) < perPage) break;
  }
  return users;
}

function normalizeEmail(email: string | null | undefined) {
  return (email ?? "").trim().toLowerCase();
}

/** Admin: list every subscription with owner email. */
export const adminListSubscriptions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: subs, error } = await supabaseAdmin
      .from("subscriptions")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    const ids = Array.from(new Set((subs ?? []).map((s: any) => s.user_id)));
    const { data: profiles } = ids.length
      ? await supabaseAdmin.from("profiles").select("user_id, email, display_name").in("user_id", ids)
      : { data: [] as any[] };
    const map = new Map((profiles ?? []).map((p: any) => [p.user_id, p]));
    return (subs ?? []).map((s: any) => ({
      ...s,
      source: s.paddle_subscription_id?.startsWith("manual_") ? "manual" : "paddle",
      user_email: map.get(s.user_id)?.email ?? null,
      user_display_name: map.get(s.user_id)?.display_name ?? null,
    }));
  });

/** Admin: subscription counts + MRR by env. */
export const adminSubscriptionStats = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const supabaseAdmin = await getSupabaseAdmin();
    const { data, error } = await supabaseAdmin
      .from("subscriptions")
      .select("product_id, status, environment, current_period_end");
    if (error) throw new Error(error.message);
    const now = new Date();
    const isActive = (s: any) =>
      (["active", "trialing", "past_due"].includes(s.status) &&
        (!s.current_period_end || new Date(s.current_period_end) > now)) ||
      (s.status === "canceled" && s.current_period_end && new Date(s.current_period_end) > now);
    const out = { live: { active: 0, mrr: 0 }, sandbox: { active: 0, mrr: 0 } };
    for (const s of data ?? []) {
      if (!isActive(s)) continue;
      const env = s.environment === "live" ? "live" : "sandbox";
      out[env].active += 1;
      const tier = (TIERS as Record<string, { monthlyUsd: number }>)[s.product_id];
      if (tier) out[env].mrr += tier.monthlyUsd;
    }
    return out;
  });

/** Admin: grant or change a user's plan (creates a manual subscription row). */
export const adminGrantPlan = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({
      userId: z.string().uuid(),
      tier: z.enum(["operator_plan", "analyst_plan", "command_plan"]),
      days: z.number().int().min(0).max(3650), // 0 = no expiry
      environment: z.enum(["sandbox", "live"]).default("live"),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const supabaseAdmin = await getSupabaseAdmin();
    const t = TIERS[data.tier as TierSlug];
    const periodEnd = data.days === 0
      ? null
      : new Date(Date.now() + data.days * 86400_000).toISOString();
    const rand = crypto.randomUUID();
    const { error } = await supabaseAdmin.from("subscriptions").insert({
      user_id: data.userId,
      paddle_subscription_id: `manual_${rand}`,
      paddle_customer_id: `manual_${data.userId}`,
      product_id: data.tier,
      price_id: t.priceId,
      status: "active",
      current_period_start: new Date().toISOString(),
      current_period_end: periodEnd,
      cancel_at_period_end: false,
      environment: data.environment,
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Admin: revoke a subscription immediately. */
export const adminRevokePlan = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ subscriptionId: z.string().uuid() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const supabaseAdmin = await getSupabaseAdmin();
    const { error } = await supabaseAdmin
      .from("subscriptions")
      .update({
        status: "canceled",
        current_period_end: new Date().toISOString(),
        cancel_at_period_end: true,
      })
      .eq("id", data.subscriptionId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Admin: list every scan ever performed (global, not user-scoped). */
export const adminListAllScans = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const supabaseAdmin = await getSupabaseAdmin();
    const { data, error } = await supabaseAdmin
      .from("intel_scan_runs")
      .select("id, user_id, target, target_type, status, risk_score, confidence, source_count, created_at, completed_at, error")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    const userIds = Array.from(new Set((data ?? []).map((scan: any) => scan.user_id).filter(Boolean)));
    const { data: profiles, error: profilesError } = userIds.length
      ? await supabaseAdmin
          .from("profiles")
          .select("user_id, email, display_name")
          .in("user_id", userIds)
      : { data: [] as any[], error: null };
    if (profilesError) throw new Error(profilesError.message);
    const profileByUser = new Map((profiles ?? []).map((profile: any) => [profile.user_id, profile]));
    return (data ?? []).map((scan: any) => {
      const profile = scan.user_id ? profileByUser.get(scan.user_id) : null;
      return {
        ...scan,
        user_email: profile?.email ?? null,
        user_display_name: profile?.display_name ?? null,
      };
    });
  });

/** Admin: list every profile (user directory). */
export const adminListProfiles = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const supabaseAdmin = await getSupabaseAdmin();
    const { data, error } = await supabaseAdmin
      .from("profiles")
      .select("user_id, email, display_name, created_at")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

/** Admin: list every private source across all users. */
export const adminListPrivateSources = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const supabaseAdmin = await getSupabaseAdmin();
    const { data, error } = await supabaseAdmin
      .from("private_sources")
      .select("id, owner_user_id, name, kind, enabled, created_at, updated_at")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

/** Admin: high-level counts for the control room. */
export const adminGetStats = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const supabaseAdmin = await getSupabaseAdmin();
    const [scans, subs, sources, requests, authUsers] = await Promise.all([
      supabaseAdmin.from("intel_scan_runs").select("id", { count: "exact", head: true }),
      supabaseAdmin.from("email_subscribers").select("id", { count: "exact", head: true }),
      supabaseAdmin.from("private_sources").select("id", { count: "exact", head: true }),
      supabaseAdmin.from("access_requests").select("id, email, status"),
      listAllAuthUsers(supabaseAdmin),
    ]);
    if (requests.error) throw new Error(requests.error.message);
    const authEmails = new Set(authUsers.map((user: any) => normalizeEmail(user.email)).filter(Boolean));
    const requestRows = requests.data ?? [];
    const unregisteredRequests = requestRows.filter((request: any) => !authEmails.has(normalizeEmail(request.email)));
    const openAccess = requestRows.filter((request: any) => request.status === "pending" || request.status === "approved").length;
    return {
      scans: scans.count ?? 0,
      subscribers: subs.count ?? 0,
      users: authUsers.length + unregisteredRequests.length,
      privateSources: sources.count ?? 0,
      pendingAccess: openAccess,
    };
  });